import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, ShoppingBag, Shield, Eye, Heart, Sparkles, Loader2, BadgeCheck, Truck, Wallet,
  Smartphone, Shirt, Home as HomeIcon, UtensilsCrossed, Gem, Clock, Store,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { HeroSlider } from "@/components/home/HeroSlider";

interface PublicProduct {
  id: string;
  title: string;
  price: number;
  currency: string;
  images: string[];
  views: number;
  likes: number;
  business_name: string | null;
  category: string;
  created_at: string;
}

const CATEGORIES = [
  { label: "Fashion", slug: "fashion", icon: Shirt },
  { label: "Beauty", slug: "beauty", icon: Sparkles },
  { label: "Electronics", slug: "electronics", icon: Smartphone },
  { label: "Home", slug: "home", icon: HomeIcon },
  { label: "Food", slug: "food", icon: UtensilsCrossed },
  { label: "Jewelry", slug: "jewelry", icon: Gem },
];

const BUYER_BENEFITS = [
  { icon: BadgeCheck, title: "Verified Sellers", text: "Shop from sellers whose identity has been checked." },
  { icon: Wallet, title: "Pay on Delivery", text: "Order now and pay only when your package arrives." },
  { icon: Shield, title: "Escrow Protection", text: "Paid online? Your money is held until you confirm delivery." },
  { icon: Truck, title: "Nationwide Tracking", text: "Follow every order from the seller to your door." },
];

const BUYER_STEPS = [
  { title: "Browse", description: "Find products from trusted sellers" },
  { title: "Order", description: "Add to cart or Buy Now in seconds" },
  { title: "Pay your way", description: "Pay online or on delivery" },
  { title: "Confirm receipt", description: "Seller is paid only after you confirm" },
];

const SELLER_STEPS = [
  { title: "Sign Up", description: "Create your seller account in seconds" },
  { title: "Add Products", description: "Upload your products with photos and prices" },
  { title: "Share on WhatsApp", description: "Post products to your WhatsApp Status" },
  { title: "Receive Orders", description: "Get orders and payments directly" },
];

function ProductGrid({ products, newBadge }: { products: PublicProduct[]; newBadge?: boolean }) {
  const navigate = useNavigate();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
      {products.map((product) => (
        <Card
          key={product.id}
          className="group overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
          onClick={() => navigate(`/products/${product.id}`)}
        >
          <div className="aspect-square relative overflow-hidden bg-muted">
            {product.images?.[0] ? (
              <img
                src={product.images[0]}
                alt={product.title}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                <ShoppingBag className="h-12 w-12" />
              </div>
            )}
            {(newBadge || product.category) && (
              <Badge className="absolute top-2 left-2 text-xs bg-background/90 text-foreground backdrop-blur-sm border shadow-sm max-w-[85%] truncate">
                {newBadge ? "New" : product.category}
              </Badge>
            )}
          </div>
          <CardContent className="p-3">
            <h3 className="font-medium text-sm line-clamp-1 group-hover:text-primary transition-colors">
              {product.title}
            </h3>
            {product.business_name && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{product.business_name}</p>
            )}
            <p className="text-primary font-bold mt-1">
              {product.currency}{Number(product.price).toLocaleString()}
            </p>
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{product.views || 0}</span>
              <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{product.likes || 0}</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState<PublicProduct[]>([]);
  const [newArrivals, setNewArrivals] = useState<PublicProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [journey, setJourney] = useState<"buy" | "sell">("buy");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Use secure RPC to avoid exposing seller_id to anon users
        const { data, error } = await supabase.rpc('get_public_products');
        if (error) throw error;

        const all: PublicProduct[] = (data || []).map((p: any) => ({
          id: p.id,
          title: p.title,
          price: p.price,
          currency: p.currency || '₦',
          images: p.images || [],
          views: p.views || 0,
          likes: p.likes || 0,
          business_name: p.business_name || null,
          category: p.category || '',
          created_at: p.created_at,
        }));

        const featured = all.slice(0, 8);
        const featuredIds = new Set(featured.map((p) => p.id));
        const newest = [...all]
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .filter((p) => !featuredIds.has(p.id))
          .slice(0, 4);

        setFeaturedProducts(featured);
        setNewArrivals(newest);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const steps = journey === "buy" ? BUYER_STEPS : SELLER_STEPS;

  return <div className="min-h-screen">
      <HeroSlider />

      {/* Category shortcuts */}
      <section className="py-8 sm:py-10 bg-background border-b">
        <div className="container mx-auto px-4">
          <h2 className="text-lg sm:text-xl font-semibold mb-4">Shop by Category</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4">
            {CATEGORIES.map(({ label, slug, icon: Icon }) => (
              <Link
                key={slug}
                to={`/products?category=${slug}`}
                className="group flex flex-col items-center gap-2 rounded-xl border bg-card p-4 hover:border-primary hover:shadow-md transition-all"
              >
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                  <Icon className="h-6 w-6 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <span className="text-sm font-medium">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-12 sm:py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h2 className="text-2xl sm:text-3xl font-bold">Featured Products</h2>
            </div>
            <Link to="/products">
              <Button variant="ghost" className="group">
                View All
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : featuredProducts.length > 0 ? (
            <ProductGrid products={featuredProducts} />
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No products available yet. Be the first to list!</p>
              <Link to="/register"><Button className="mt-4">Start Selling</Button></Link>
            </div>
          )}
        </div>
      </section>

      {/* New Arrivals */}
      {!loading && newArrivals.length > 0 && (
        <section className="pb-12 sm:pb-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                <h2 className="text-2xl sm:text-3xl font-bold">New Arrivals</h2>
              </div>
              <Link to="/products">
                <Button variant="ghost" className="group">
                  See More
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            <ProductGrid products={newArrivals} newBadge />
          </div>
        </section>
      )}

      {/* Why shop with us */}
      <section className="py-16 sm:py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Why Shop on SabiTradeNG?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Safe, simple shopping — your money is protected from order to delivery
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BUYER_BENEFITS.map(({ icon: Icon, title, text }) => (
              <Card key={title} className="p-5 sm:p-6 hover:shadow-lg transition-shadow">
                <div className="h-12 w-12 rounded-lg bg-gradient-primary flex items-center justify-center mb-4">
                  <Icon className="h-6 w-6 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{title}</h3>
                <p className="text-muted-foreground text-sm">{text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">How It Works</h2>
            <div className="inline-flex rounded-full border bg-muted p-1">
              {(["buy", "sell"] as const).map((j) => (
                <button
                  key={j}
                  onClick={() => setJourney(j)}
                  className={`px-4 sm:px-6 py-2 rounded-full text-sm font-medium transition-all ${
                    journey === j ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {j === "buy" ? "I want to buy" : "I want to sell"}
                </button>
              ))}
            </div>
          </div>
          <div key={journey} className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8 animate-fade-in">
            {steps.map((item, i) => (
              <div key={item.title} className="text-center">
                <div className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 rounded-full bg-gradient-primary flex items-center justify-center mx-auto mb-3 md:mb-4 text-lg sm:text-xl md:text-2xl font-bold text-primary-foreground">
                  {i + 1}
                </div>
                <h3 className="font-semibold mb-1 sm:mb-2 text-sm sm:text-base">{item.title}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground px-2 sm:px-0">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust markers */}
      <section className="py-12 sm:py-16 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">100%</div>
              <div className="text-sm sm:text-base text-muted-foreground">Escrow-protected online payments</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-whatsapp mb-2">35 + FCT</div>
              <div className="text-sm sm:text-base text-muted-foreground">States we deliver to</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">Every order</div>
              <div className="text-sm sm:text-base text-muted-foreground">Trackable from seller to your door</div>
            </div>
          </div>
        </div>
      </section>

      {/* Split CTA */}
      <section className="grid grid-cols-1 md:grid-cols-2">
        <div className="py-12 sm:py-16 px-6 bg-gradient-primary text-center">
          <ShoppingBag className="h-10 w-10 mx-auto mb-4 text-primary-foreground" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-primary-foreground">Ready to Shop?</h2>
          <p className="text-primary-foreground/90 mb-6 max-w-md mx-auto">
            Discover great products and pay online or on delivery.
          </p>
          <Link to="/products">
            <Button size="xl" variant="glass" className="group">
              Shop Now
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
        <div className="py-12 sm:py-16 px-6 bg-gradient-whatsapp text-center">
          <Store className="h-10 w-10 mx-auto mb-4 text-whatsapp-foreground" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-whatsapp-foreground">Want to Sell?</h2>
          <p className="text-whatsapp-foreground/90 mb-6 max-w-md mx-auto">
            Turn your WhatsApp Status into a store and get paid securely.
          </p>
          <Link to="/register">
            <Button size="xl" variant="glass" className="group">
              Start Selling Free
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>;
}
